import FVector from '@data/geometry/FVector';
import { BinaryReader } from 'Loader/serialization/common/BinaryReader';
import HeaderSerializer from 'Loader/serialization/serializers/v0_0_13/binaryData/HeaderSerializer';
import Header from '../common/Header';
import { findSerializer } from '../common/SerializerFinder';
import { SerializerKind } from '../common/Constants';
import { Jumper } from './Jumper.js';

type MultiLine = {
    value?: number;
    points: Point[];
};

type Point = {
    point: FVector;
    value?: number;
};

export abstract class MultiLineJumper extends Jumper {
    protected binaryReader: BinaryReader;
    protected headerSerializer: HeaderSerializer;
    override kind: 'multiline' = 'multiline';

    constructor(reader: BinaryReader, headerSerializer: HeaderSerializer) {
        super();
        this.binaryReader = reader;
        this.headerSerializer = headerSerializer;
    }

    abstract getMultiLines(sp: number): MultiLine[];

    override dispose(): void {
        this.binaryReader = undefined as unknown as BinaryReader;
        this.headerSerializer = undefined as unknown as HeaderSerializer;
    }
}

export async function getMultiLineJumper(binaryReader: BinaryReader) {
    const vMajor = binaryReader.readInt32();
    const vMinor = binaryReader.readInt32();
    const vRelease = binaryReader.readInt32();
    const readVersion = `v${vMajor}_${vMinor}_${vRelease}`;

    if (vMajor != 0 || vMinor != 0 || vRelease > 14) {
        throw new Error("Serializer don't exists");
    }

    const serializerHeaderInstance: any = findSerializer(readVersion, Header, SerializerKind.BINARY_DATA).serializer;

    let jumper;
    if (vRelease < 14) jumper = new MultiLineJumperV0_0_1(binaryReader, serializerHeaderInstance);
    else jumper = new MultiLineJumperV0_0_14(binaryReader, serializerHeaderInstance);

    binaryReader.reset();
    await jumper.init();
    return jumper;
}

export class MultiLineJumperV0_0_1 extends MultiLineJumper {
    private adjustedMin: number = 0;
    private adjustedMax: number = 0;
    private readNext: boolean = false;
    private pointsPoolPosition: number = 0;
    private multiLinesPosition: number[] = [];
    private pointsValuesPosition: number = 0;

    constructor(reader: BinaryReader, headerSerializer: HeaderSerializer) {
        super(reader, headerSerializer);
    }

    override dispose(): void {
        this.multiLinesPosition = [];
        this.readNext = false;
        this.pointsPoolPosition = 0;
        this.pointsValuesPosition = 0;
        this.adjustedMin = 0;
        this.adjustedMax = 0;
        super.dispose();
    }

    protected async readHeader() {
        const headerData = await this.headerSerializer.read(this.binaryReader);
        return {
            nSP: headerData.numSensPnts,
        };
    }

    protected readDimensions() {
        const nPoints = this.binaryReader.readUInt32();
        return {
            nPoints,
        };
    }

    override async init() {
        const { nSP } = await this.readHeader();
        const { nPoints } = this.readDimensions();
        this.multiLinesPosition = new Array(nSP);
        const values = [];

        this.pointsPoolPosition = this.binaryReader.getPos();
        this.binaryReader.skip(4 * 3 * nPoints);
        this.readNext = this.binaryReader.readBoolean();

        if (this.readNext) {
            this.pointsValuesPosition = this.binaryReader.getPos();
            for (let i = 0; i < nPoints; i++) {
                const value = this.binaryReader.readFloat32();
                values.push(Math.abs(value));
            }
        }

        for (let i = 0; i < nSP; i++) {
            this.multiLinesPosition[i] = this.binaryReader.getPos();
            const nMultiLines = this.binaryReader.readUInt32();
            if (this.readNext) {
                this.binaryReader.skip(nMultiLines * 4 * 2);
            } else {
                for (let j = 0; j < nMultiLines; j++) {
                    const value = this.binaryReader.readFloat32();
                    values.push(Math.abs(value));
                    this.binaryReader.skip(4 * 2);
                }
            }
        }

        this.adjustedMin = this.calculatePercentile(values, 5);
        this.adjustedMax = this.calculatePercentile(values, 95);
    }

    override getMin(): number {
        return this.adjustedMin;
    }

    override getMax(): number {
        return this.adjustedMax;
    }

    override getMultiLines(sp: number): MultiLine[] {
        let multiLines: MultiLine[] = [];
        this.binaryReader.setPos(this.multiLinesPosition[sp]);
        if (this.readNext) {
            const nMultiLines = this.binaryReader.readUInt32();
            multiLines = new Array(nMultiLines);
            for (let i = 0; i < nMultiLines; i++) {
                this.binaryReader.setPos(this.multiLinesPosition[sp] + 4 + 4 * 2 * i);
                const a = this.binaryReader.readUInt32();
                const b = this.binaryReader.readUInt32();

                const points = this.getPointsFromPool(a, b);

                this.binaryReader.setPos(this.pointsValuesPosition + a * 4);
                for (let j = a; j <= b; j++) {
                    points[j - a].value = this.adjustValue(this.binaryReader.readFloat32());
                }

                multiLines[i] = { points };
            }
        } else {
            const nMultiLines = this.binaryReader.readUInt32();
            multiLines = new Array(nMultiLines);
            for (let i = 0; i < nMultiLines; i++) {
                this.binaryReader.setPos(this.multiLinesPosition[sp] + 4 + 4 * 3 * i);
                const value = this.adjustValue(this.binaryReader.readFloat32());
                const a = this.binaryReader.readUInt32();
                const b = this.binaryReader.readUInt32();

                const points = this.getPointsFromPool(a, b);
                multiLines[i] = { value, points };
            }
        }

        return multiLines;
    }

    private getPointsFromPool(a: number, b: number) {
        this.binaryReader.setPos(this.pointsPoolPosition + a * 4 * 3);
        const points: Point[] = new Array(b - a + 1);
        for (let j = a; j <= b; j++) {
            points[j - a] = {
                point: this.binaryReader.readFVector(),
            };
        }
        return points;
    }

    private calculatePercentile(data: number[], percentile: number) {
        const sortedData = [...data].sort((a, b) => a - b);
        const index = (percentile / 100) * (sortedData.length - 1);
        const lower = Math.floor(index);
        const upper = Math.ceil(index);
        return lower === upper ? sortedData[lower] : sortedData[lower] + (index - lower) * (sortedData[upper] - sortedData[lower]);
    }

    private adjustValue(value: number): number {
        if (value < this.adjustedMin) return this.adjustedMin;
        if (value > this.adjustedMax) return this.adjustedMax;
        return value;
    }
}

export class MultiLineJumperV0_0_14 extends MultiLineJumperV0_0_1 {
    protected override readDimensions() {
        this.binaryReader.readUInt32();
        const nPoints = this.binaryReader.readUInt32();
        return {
            nPoints,
        };
    }
}
